<script setup lang="ts">
import { Search } from '@element-plus/icons-vue';
import {
  ref, watch,
} from 'vue';
import { debounce } from '../../shared/utility-functions';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'search']);

const search = ref(props.modelValue);

const debouncedSearch = ref(search.value);

watch(
  search,
  debounce((newValue) => {
    debouncedSearch.value = newValue;
    emit('update:modelValue', newValue);
    emit('search', newValue);
  }, 300),
);

watch(() => props.modelValue, (newValue) => {
  search.value = newValue;
});

</script>

<template lang="pug">
.c-consent-filters
  .c-consent-filters__search
    el-input(
      v-model="search"
      :prefix-icon="Search"
      type="text"
      placeholder="Search by user name..."
      clearable
      size="large"
    )
</template>

<style lang="scss" scoped>
// Color Variables - Match Dashboard.vue
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$color-purple-text: #4349dd;

.c-consent-filters {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 0;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }

  &__search {
    flex: 1;
    min-width: 0;

    :deep(.el-input) {
      .el-input__wrapper {
        border-radius: 10px;
        border: 1.5px solid $gray-200;
        background: #F0F0F3;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
        padding: 0 12px;
        height: 40px;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

        &:hover {
          border-color: $color-purple-text;
          box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.08);
        }

        &.is-focus {
          border-color: $color-purple-text;
          box-shadow: 0 0 0 3px rgba(67, 73, 221, 0.12), 0 2px 4px 0 rgba(67, 73, 221, 0.08);
        }
      }

      .el-input__inner {
        height: 100%;
        font-size: 0.875rem;
        color: $gray-700;
        background: transparent;
        
        &::placeholder {
          color: $gray-400;
          font-weight: 400;
        }
      }

      .el-input__prefix {
        color: $gray-400;
        font-size: 1.125rem;
        
        .el-icon {
          font-size: 1.125rem;
        }
      }

      .el-input__suffix {
        .el-input__clear {
          color: $gray-400;
          transition: color 0.2s ease;
          
          &:hover {
            color: $color-purple-text;
          }
        }
      }
    }
  }
}
</style>
